import { HrefLink as Link } from "@/components/ui/href-link";
import { RequirePermission } from "@/hooks/shared/CheckAuth";
import { createFileRoute } from "@tanstack/react-router";
import { CalendarClock, ChevronLeft, ChevronRight } from "lucide-react";

import { EmptyState } from "@/components/dashboard/data-table/empty-state";
import { PageHeader } from "@/components/dashboard/page-header";
import { StatusPill } from "@/components/dashboard/status-pill";
import { RotaCell } from "@/components/shifts/rota-cell";
import { WeekActions } from "@/components/shifts/week-actions";
import { ZonePicker } from "@/components/shifts/zone-picker";
import { nav, shifts as copy } from "@/content";
import { can, scopeToSupervisorId } from "@/constants/permissions";
import { buildQuery, readNumber } from "@/utils/query-params";
import { getRota, listTemplates } from "@/api/shifts";
import { addDateKey, instantAt, isWeekend, resolveWeekKey, toDateKey } from "@/utils/time";
import { cn } from "@/utils";
import { useQuery } from "@tanstack/react-query";
import { useQueryParams } from "@/hooks/shared/useQueryParams";
import { useFmt } from "@/hooks/shared/useFmt";
import { useDocumentTitle } from "@/hooks/shared/useDocumentTitle";
import { useSession } from "@/context/UserContext";
import { PageError, PageLoading } from "@/components/dashboard/page-state";

const BASE_PATH = "/dashboard/shifts";

/**
 * The rota: zones down, days across.
 *
 * The grid is `gap-px` over a `bg-n4` ground, so the lines between cells *are*
 * the gaps — the design system's sectioning device, and the only way seventy
 * cells stay legible. Nested cards at this density would be unreadable.
 *
 * Server-rendered in full. Sorting, filtering and the week are all in the URL, so
 * there is no client-side model of the rota to keep in sync; the only client
 * components are the cell panels, mounted one at a time.
 *
 * This screen is what replaces the prototype's entire approach to scheduling: a
 * single `shiftStart`/`shiftEnd` pair of strings on each user row. That could not
 * express a second pattern, a headcount per site, or a night shift whose end is
 * before its start — and it is what its performance page compared real punches
 * against.
 */
export const Route = createFileRoute("/_main/dashboard/shifts/")({
  component: () => (
    <RequirePermission action="shifts.read">
      <ShiftsPage />
    </RequirePermission>
  ),
});

function ShiftsPage() {
  const { user, settings } = useSession();
  useDocumentTitle(copy.title);
  const params = useQueryParams();
  const fmt = useFmt();

  const weekStartKey = resolveWeekKey(
    typeof params.week === "string" ? params.week : undefined,
    new Date(),
    settings.timeZone,
  );
  const zoneId = readNumber(params, "zone");

  const rotaFilters = {
    weekStartKey,
    timeZone: settings.timeZone,
    zoneId,
    scopeToSupervisorId: scopeToSupervisorId(user),
  };

  const rotaQuery = useQuery({
    queryKey: ["shifts", "rota", rotaFilters],
    queryFn: () => getRota(rotaFilters),
  });
  const templatesQuery = useQuery({
    queryKey: ["shifts", "templates", { showArchived: false }],
    queryFn: () => listTemplates(),
    staleTime: 60_000,
  });

  /* The rota is the screen; a half-drawn grid would read as an empty week. */
  if (rotaQuery.isPending) return <PageLoading />;
  if (rotaQuery.isError && !rotaQuery.data) {
    return (
      <PageError
        error={rotaQuery.error}
        onRetry={() => {
          void rotaQuery.refetch();
        }}
      />
    );
  }
  if (!rotaQuery.data) return <PageLoading />;

  const rota = rotaQuery.data;
  const templates = templatesQuery.data ?? [];

  const canWrite = can(user.role, "shifts.write");
  const canPublish = can(user.role, "shifts.publish");

  const thisWeekKey = resolveWeekKey(undefined, new Date(), settings.timeZone);
  const previousKey = addDateKey(weekStartKey, -7, settings.timeZone);
  const nextKey = addDateKey(weekStartKey, 7, settings.timeZone);

  /* Every zone gets a row, including one with no shifts this week — an absent row
     would make an unstaffed site invisible, which is the opposite of what a
     coverage board is for. */
  const shiftsByCell = new Map<string, typeof rota.shifts>();
  for (const shift of rota.shifts) {
    const key = `${shift.zoneId}:${shift.dateKey}`;
    shiftsByCell.set(key, [...(shiftsByCell.get(key) ?? []), shift]);
  }

  const templateOptions = templates.map((template) => ({
    id: template.id,
    name: template.name,
    startTime: template.startTime,
    endTime: template.endTime,
    crossesMidnight: template.crossesMidnight,
    color: template.color,
  }));

  const weekLabel = `${fmt.dateNumeric(instantAt(weekStartKey, "12:00", settings.timeZone))} – ${fmt.dateNumeric(instantAt(rota.dateKeys[6], "12:00", settings.timeZone))}`;

  const navClass =
    "grid size-10 place-items-center rounded-lg border border-n4 bg-n0 text-n7 transition-colors duration-200 ease-brand hover:border-n5 hover:text-n9";

  return (
    <div className="mx-auto w-full max-w-shell px-6 py-8">
      <PageHeader
        eyebrow={nav.groups.operations}
        title={copy.title}
        lead={copy.lead}
        actions={
          <div className="flex flex-wrap items-center gap-2">
            <Link
              to={`${BASE_PATH}/templates`}
              className="inline-flex h-10 items-center gap-2 rounded-lg border border-n4 bg-n0 px-4 text-sm font-semibold text-n8 transition-colors duration-200 ease-brand hover:bg-n2"
            >
              <CalendarClock aria-hidden className="size-4" strokeWidth={1.6} />
              {copy.templatesLink}
            </Link>
            {canWrite ? (
              <WeekActions
                weekStartKey={weekStartKey}
                zoneId={zoneId}
                draftCount={rota.summary.draft}
                gapCount={rota.summary.draftGaps}
                numerals={user.numeralSystem}
                canPublish={canPublish}
              />
            ) : null}
          </div>
        }
      />

      {!canWrite ? <p className="mt-5 text-xs text-n6">{copy.readOnlyNote}</p> : null}

      {/* Week navigation and the state of the week, on one line. Chevrons point
          the way the reader travels: under RTL, "next" is leftward. */}
      <div className="mt-6 flex flex-wrap items-end justify-between gap-4">
        <div className="flex items-end gap-2">
          <Link
            to={`${BASE_PATH}${buildQuery(params, { week: previousKey })}`}
            aria-label={copy.previousWeek}
            className={navClass}
          >
            <ChevronRight aria-hidden className="size-4" strokeWidth={1.6} />
          </Link>
          <Link
            to={`${BASE_PATH}${buildQuery(params, { week: nextKey })}`}
            aria-label={copy.nextWeek}
            className={navClass}
          >
            <ChevronLeft aria-hidden className="size-4" strokeWidth={1.6} />
          </Link>

          <div className="ms-2">
            <p className="text-[11px] font-semibold text-n6">{copy.weekOf}</p>
            <p className="tabular text-sm font-bold text-n9">{weekLabel}</p>
          </div>

          {weekStartKey !== thisWeekKey ? (
            <Link
              to={`${BASE_PATH}${buildQuery(params, { week: null })}`}
              className="ms-2 text-xs font-semibold text-orange-700 transition-opacity duration-200 ease-brand hover:opacity-80"
            >
              {copy.thisWeek}
            </Link>
          ) : null}
        </div>

        <div className="flex flex-wrap items-end gap-4">
          <ZonePicker
            params={params}
            basePath={BASE_PATH}
            zones={rota.zones}
            value={zoneId ? String(zoneId) : ""}
          />
        </div>
      </div>

      {/* The week in one line of numbers. Gaps get the accent only when there
          are some — a permanently orange counter reading zero is noise. */}
      <div className="mt-5 flex flex-wrap items-center gap-2">
        <Summary label={copy.summaryShifts} value={fmt.count(rota.summary.shifts)} />
        <Summary label={copy.summaryAssigned} value={fmt.count(rota.summary.assigned)} />
        <Summary label={copy.summaryRequired} value={fmt.count(rota.summary.required)} />
        {rota.summary.gaps > 0 ? (
          <StatusPill tone="attention">
            {copy.summaryGaps}: <span className="tabular">{fmt.count(rota.summary.gaps)}</span>
          </StatusPill>
        ) : null}
        {rota.summary.draft > 0 ? (
          <StatusPill tone="neutral">
            {copy.summaryDrafts}: <span className="tabular">{fmt.count(rota.summary.draft)}</span>
          </StatusPill>
        ) : rota.summary.shifts > 0 ? (
          <StatusPill tone="positive">{copy.allPublished}</StatusPill>
        ) : null}
      </div>

      {rota.zones.length === 0 ? (
        <div className="mt-8 rounded-lg border border-n3 bg-n0 py-16 shadow-1">
          <EmptyState
            variant="empty"
            title={copy.title}
            lead={
              scopeToSupervisorId(user) !== null ? copy.noZonesScoped : copy.noZones
            }
            actionHref={can(user.role, "zones.write") ? "/dashboard/zones/new" : undefined}
            actionLabel={can(user.role, "zones.write") ? copy.addShift : undefined}
          />
        </div>
      ) : (
        /* Scrolls inside its own container, so a seven-day grid never makes the
           page scroll sideways — the commonest RTL layout failure. */
        <div className="mt-6 overflow-hidden rounded-lg border border-n3 bg-n0 shadow-1">
          <div className="overflow-x-auto">
            <div
              className="grid min-w-[62rem] gap-px bg-n4"
              style={{ gridTemplateColumns: "11rem repeat(7, minmax(0, 1fr))" }}
            >
              {/* Header row */}
              <div className="bg-n2 px-3 py-2.5 text-[11px] font-semibold text-n7">
                {copy.gridZoneColumn}
              </div>
              {rota.dateKeys.map((dateKey) => {
                const instant = instantAt(dateKey, "12:00", settings.timeZone);
                const today = dateKey === toDateKey(new Date(), settings.timeZone);
                return (
                  <div
                    key={dateKey}
                    className={cn(
                      "px-3 py-2.5 text-[11px] font-semibold",
                      today ? "bg-orange/12 text-orange-700" : "bg-n2 text-n7",
                    )}
                  >
                    <span className="block">{fmt.weekday(instant)}</span>
                    <span className="tabular mt-0.5 block text-[10px] font-normal text-n6">
                      {fmt.dateNumeric(instant)}
                    </span>
                  </div>
                );
              })}

              {/* One row per zone */}
              {rota.zones.map((zone) => (
                <div key={zone.id} className="contents">
                  <div className="flex flex-col justify-center gap-1 bg-n1 px-3 py-2.5">
                    <Link
                      to={`/dashboard/zones/${zone.id}`}
                      className="flex items-center gap-2 text-xs font-semibold text-n8 transition-colors duration-200 ease-brand hover:text-orange-700"
                    >
                      <span
                        aria-hidden
                        className="size-2 shrink-0 rounded-sm"
                        style={{ backgroundColor: zone.color }}
                      />
                      <span className="min-w-0 truncate">{zone.name}</span>
                    </Link>
                  </div>

                  {rota.dateKeys.map((dateKey) => (
                    <RotaCell
                      key={`${zone.id}:${dateKey}`}
                      zoneId={zone.id}
                      zoneName={zone.name}
                      zoneRequiredGuards={zone.requiredGuards}
                      dateKey={dateKey}
                      dayLabel={`${fmt.weekday(instantAt(dateKey, "12:00", settings.timeZone))} ${fmt.dateNumeric(instantAt(dateKey, "12:00", settings.timeZone))}`}
                      shifts={shiftsByCell.get(`${zone.id}:${dateKey}`) ?? []}
                      templates={templateOptions}
                      numerals={user.numeralSystem}
                      timeZone={settings.timeZone}
                      canWrite={canWrite}
                      weekend={isWeekend(dateKey, settings.timeZone)}
                    />
                  ))}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {rota.zones.length > 0 && rota.summary.shifts === 0 ? (
        <p className="mt-4 text-xs text-n6">{copy.noShiftsThisWeek}</p>
      ) : null}

      {templates.length === 0 && canWrite ? (
        <p className="mt-4 text-xs font-semibold text-orange-700">
          <Link to={`${BASE_PATH}/templates`} className="hover:opacity-80">
            {copy.templatesEmptyLead}
          </Link>
        </p>
      ) : null}
    </div>
  );
}

function Summary({ label, value }: { label: string; value: string }) {
  return (
    <span className="inline-flex items-baseline gap-1.5 rounded-md border border-n3 bg-n0 px-2.5 py-1.5">
      <span className="text-[11px] font-semibold text-n6">{label}</span>
      <span className="tabular text-sm font-bold text-n9">{value}</span>
    </span>
  );
}
